import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { createSubAgentDefinition } from '../services/agentApi';
import {
    IMPLEMENTATION_STATUS,
    buildReportPlanReturnPath,
    upsertImplementation,
} from '../utils/reportPlanImplementations';
import { ArrowLeft, Loader, Save, Eye, Edit3, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Canonical sub-agent markdown (matches SUB_AGENT_CREATION_PROMPT / AGENT_CREATION_SYSTEM_MESSAGE).
 * Frontmatter: description, model, focus, insight_types — tables live in # Allowed Tables body only.
 */
function buildCanonicalAgentMarkdown(agentDisplayName) {
    const n = (agentDisplayName || 'New Sub-Agent').trim() || 'New Sub-Agent';
    return `---
description: One sentence — what this agent analyzes and which decisions it supports.
model: sonnet
focus: The single business domain this agent owns (no overlap with other agents).
insight_types:
  - overview
  - trends
---

# Mission
${n} specializes in one clear domain. Replace this paragraph with 2–4 sentences on who the agent is, what data it uses, and what business decisions it supports.

# Domain Focus
Replace with one short paragraph naming the domain only this agent should own.

# AI Insight Summary
Replace with one rich paragraph (4–8 sentences): name real tables/columns/metrics, typical joins or filters, and the KPIs or insights you expect. Stay grounded in your schema — no invented objects.

# Allowed Tables
- **replace_with_real_table_name** (primary) - Why this agent owns this table
- **another_table** - Role in this domain (use **bold** table names)

## Skills

### Allowed
- Concrete capability tied to allowed tables (short inline SQL is OK, e.g. \`AVG(column)\` on a real column)
- Another allowed analysis or reporting behavior

### Restricted
- What this agent must not do; defer to another agent or domain when relevant

## Allowed analysis

### Example category one
- Specific metric or question + grouping or filter, grounded in real columns
- Another bullet for this category

### Example category two
- Add 3–6 \`###\` categories total; 2–5 bullets each

## Guardrails

### Response control
- Mandatory filters, joins, or grain rules (real SQL snippets allowed when helpful)
- Another technical constraint

### Scope restriction
- Explicitly out-of-scope areas or sibling agents that own other domains

# Hard Scope Rules
**DO NOT analyze:**
- Topics outside this agent's tables or domain

**DO NOT:**
- Fabricate data; assume only what the schema supports

# Output Rules
- Use clear business language; tie metrics to entities from the Allowed Tables section.
- Say when data is missing; do not invent tables or columns.
- Keep answers within this agent's guardrails and Allowed Tables list.
`;
}

const DEFAULT_AGENT_MARKDOWN = buildCanonicalAgentMarkdown('New Sub-Agent');

function splitFrontmatter(markdown) {
    const text = markdown || '';
    if (!text.startsWith('---')) {
        return { hasFrontmatter: false, frontmatter: '', body: text };
    }
    const lines = text.split('\n');
    if (lines.length < 3) return { hasFrontmatter: false, frontmatter: '', body: text };
    // first line is ---
    let endIdx = -1;
    for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim() === '---') {
            endIdx = i;
            break;
        }
    }
    if (endIdx === -1) return { hasFrontmatter: false, frontmatter: '', body: text };
    const fm = lines.slice(1, endIdx).join('\n');
    const body = lines.slice(endIdx + 1).join('\n');
    return { hasFrontmatter: true, frontmatter: fm, body };
}

/**
 * SubAgentCreate - Markdown-first sub-agent creation.
 * Source of truth is `.claude/agents/*.md`.
 */
export const SubAgentCreate = () => {
    const { t } = useTranslation(['pages-misc-4', 'common']);
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const databaseFromState = location.state?.database;
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState('');
    const [agentMarkdown, setAgentMarkdown] = useState(DEFAULT_AGENT_MARKDOWN);
    const [activeTab, setActiveTab] = useState('edit'); // 'edit' or 'preview'

    const defaultMarkdown = useMemo(() => buildCanonicalAgentMarkdown(name), [name]);

    const effectiveMarkdown = agentMarkdown || defaultMarkdown;

    const handleBack = () => navigate(-1);

    const handleCreate = async () => {
        if (!databaseFromState?.id) {
            showToast(t('subAgentCreate.toastMissingDatabase'), 'error');
            return;
        }
        if (!name.trim()) {
            showToast(t('subAgentCreate.toastNameRequired'), 'error');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                database_connection_id: databaseFromState.id,
                name: name.trim(),
                agent_markdown: effectiveMarkdown.trimEnd() + '\n'
            };
            const resp = await createSubAgentDefinition(payload);
            if (resp.data?.status === 'success') {
                const newId = resp.data?.data?.id;
                const reportPlanContext = location.state?.reportPlanContext;
                if (reportPlanContext?.reportGenerationId && newId) {
                    upsertImplementation(
                        reportPlanContext.reportGenerationId,
                        reportPlanContext.recommendationIndex,
                        reportPlanContext.recommendation,
                        {
                            status: IMPLEMENTATION_STATUS.COMPLETED,
                            sub_agent_id: newId,
                            sub_agent_name: name.trim(),
                        }
                    );
                    showToast(t('subAgentCreate.toastReturningToPlan'), 'success');
                    navigate(
                        buildReportPlanReturnPath(
                            reportPlanContext.databaseId,
                            reportPlanContext.reportGenerationId
                        )
                    );
                    return;
                }
                showToast(t('subAgentCreate.toastCreated'), 'success');
                if (newId) navigate(`/agents/sub-agent/${newId}/edit`);
                else navigate(-1);
                return;
            }
            showToast(t('subAgentCreate.toastFailed'), 'error');
        } catch (e) {
            console.error('Failed to create sub-agent:', e);
            showToast(t('subAgentCreate.toastFailed'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const textareaRef = useRef(null);

    // Adaptive textarea height
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [agentMarkdown, activeTab]);

    return (
        <div className="page-content pb-20">
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('subAgentCreate.back')}
                </button>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('subAgentCreate.title')}</h1>
                        <p className="text-sm text-gray-400 mt-1 flex items-center font-medium">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 mr-2 shadow-sm shadow-primary-200"></span>
                            {t('subAgentCreate.subtitle')}
                        </p>
                    </div>
                    <button
                        onClick={handleCreate}
                        disabled={saving}
                        className={`px-6 py-2.5 rounded-xl font-bold text-[11px] uppercase tracking-widest flex items-center space-x-2 transition-all shadow-lg ${saving
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                : 'bg-primary-600 text-white hover:bg-primary-700 shadow-primary-100 hover:-translate-y-0.5 active:translate-y-0'
                            }`}
                    >
                        {saving ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                <span>{t('subAgentCreate.creating')}</span>
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                <span>{t('subAgentCreate.createAgent')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm mb-8">
                <div className="bg-gray-50/50 px-8 py-5 border-b border-gray-100">
                    <h3 className="text-[10px] font-bold text-gray-900 uppercase tracking-[0.2em]">{t('subAgentCreate.agentConfiguration')}</h3>
                </div>
                <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5">{t('subAgentCreate.agentName')}</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all bg-gray-50/30"
                                placeholder={t('subAgentCreate.agentNamePlaceholder')}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5">{t('subAgentCreate.targetDatabase')}</label>
                            <div className="w-full px-5 py-3 border border-gray-200 bg-gray-50/50 text-gray-500 rounded-xl text-sm flex items-center font-medium">
                                <div className="w-2 h-2 rounded-full bg-green-500 mr-2.5 shadow-sm shadow-green-200"></div>
                                {databaseFromState?.name || t('subAgentCreate.unknownDatabase')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-gray-50/50 px-8 py-4 border-b border-gray-100 flex items-center justify-between">
                    <label className="text-[10px] font-bold text-gray-900 uppercase tracking-[0.2em]">{t('subAgentCreate.agentInstructions')}</label>
                    <div className="flex items-center space-x-1 pb-2">
                        <button
                            onClick={() => setActiveTab('edit')}
                            className={`px-3 py-1 text-xs font-medium rounded-md flex items-center space-x-1 transition-colors ${activeTab === 'edit'
                                    ? 'bg-primary-50 text-primary-700 border border-primary-100'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <Edit3 className="w-3 h-3" />
                            <span>{t('subAgentCreate.edit')}</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('preview')}
                            className={`px-3 py-1 text-xs font-medium rounded-md flex items-center space-x-1 transition-colors ${activeTab === 'preview'
                                    ? 'bg-primary-50 text-primary-700 border border-primary-100'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <Eye className="w-3 h-3" />
                            <span>{t('subAgentCreate.preview')}</span>
                        </button>
                    </div>
                </div>

                <div className="p-8">
                    {activeTab === 'edit' ? (
                        <textarea
                            ref={textareaRef}
                            value={agentMarkdown}
                            onChange={(e) => setAgentMarkdown(e.target.value)}
                            className="w-full font-mono text-xs px-6 py-5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none bg-gray-50/30 leading-relaxed shadow-inner overflow-hidden min-h-[500px]"
                        />
                    ) : (
                        <div className="space-y-8 max-w-5xl mx-auto">
                            {/* Main Preview Area */}
                            <div className="space-y-8">
                                <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
                                    <div className="bg-gray-50/50 px-8 py-5 border-b border-gray-100 flex items-center justify-between">
                                        <h3 className="text-xs font-bold text-gray-900 flex items-center uppercase tracking-widest">
                                            <FileText className="w-4 h-4 mr-2.5 text-primary-500" />
                                            {t('subAgentCreate.agentPreview')}
                                        </h3>
                                        <div className="flex items-center space-x-2">
                                            <span className="px-2 py-1 bg-primary-50 text-primary-600 text-[10px] font-bold rounded-md border border-primary-100 uppercase tracking-tight">
                                                {t('subAgentCreate.livePreview')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="p-10">
                                        <div className="prose prose-slate max-w-none
                                            prose-headings:text-gray-900 prose-headings:font-bold prose-headings:tracking-tight
                                            prose-p:text-gray-600 prose-p:leading-relaxed
                                            prose-li:text-gray-600 prose-li:leading-relaxed
                                            prose-strong:text-gray-900 prose-strong:font-bold
                                            prose-code:text-primary-600 prose-code:bg-primary-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                                            prose-blockquote:border-l-primary-500 prose-blockquote:bg-gray-50 prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:rounded-r-2xl
                                        ">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {splitFrontmatter(effectiveMarkdown).body || t('subAgentCreate.noInstructions')}
                                            </ReactMarkdown>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex items-center justify-between px-2">
                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.2em]">
                            {t('subAgentCreate.markdownSupport')}
                        </p>
                        <p className="text-[9px] text-gray-400 font-mono font-medium">
                            {t('subAgentCreate.targetPathLabel')} .claude/agents/&lt;slug&gt;.md
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

